import React, {useMemo, useState, useRef} from 'react';
import {Box, Menu, MenuItem} from '@mui/material';
import getStyleForType from '../utils/getStyleForType';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import Divider from '@mui/material/Divider';
import {useTranslation} from "react-i18next";
import LinkOffIcon from '@mui/icons-material/LinkOff';


function HighlightOverlay({
                              pageHighlights,
                              scale,
                              onBoxClick,
                              onBoxHover,
                              selectedAnnotations,
                              isDrawing,
                              currentRect,
                              isCurrentPage,
                              onDissociateAnnotation,
                              onRemoveAnnotation,
                              onUpdateAnnotationPosition,
                              groupingTempKeys,
                              multiSelectMode,
                              peekKey,
                              hoveredKey,
                          }) {
    const {t} = useTranslation();

    // Drag-and-drop state
    const [dragging, setDragging] = useState(null);
    const [dragOffset, setDragOffset] = useState({x: 0, y: 0});
    const dragStartPos = useRef({x: 0, y: 0});

    // Resize state
    const [resizing, setResizing] = useState(null); // {annotation, box, handle, startBbox, startMouse}
    const [resizePreview, setResizePreview] = useState(null); // {left, top, width, height}
    const [resizeHoverKey, setResizeHoverKey] = useState(null);
    const resizeHoverTimerRef = useRef(null);

    const MARGIN = 2;

    const [contextMenu, setContextMenu] = useState(null);
    const [contextAnnotation, setContextAnnotation] = useState(null);
    const [contextBoxText, setContextBoxText] = useState('');

    const showResizeAffordance = (key) => {
        if (resizeHoverTimerRef.current) {
            clearTimeout(resizeHoverTimerRef.current);
            resizeHoverTimerRef.current = null;
        }
        setResizeHoverKey(key);
    };

    const hideResizeAffordanceSoon = () => {
        if (resizeHoverTimerRef.current) clearTimeout(resizeHoverTimerRef.current);
        resizeHoverTimerRef.current = setTimeout(() => {
            setResizeHoverKey(null);
            resizeHoverTimerRef.current = null;
        }, 180);
    };

    const handleGroupContextMenu = (event, groupId) => {
        event.preventDefault();
        setContextMenu({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
        });
        // Set a special object to signify this is a group action
        setContextAnnotation({groupId, isGroupAction: true});
        // No specific text to copy for a whole group
        setContextBoxText('');
    };

    const isAnnotationSelected = (annotation) =>
        (selectedAnnotations ?? []).some(a => JSON.stringify(a) === JSON.stringify(annotation));

    const peekSet = useMemo(() => {
        if (!peekKey) return new Set();
        return new Set(Array.isArray(peekKey) ? peekKey : [peekKey]);
    }, [peekKey]);

// HighlightOverlay.js
    const getAnnotationKey = (a) => {
        if (a.__uid) return a.__uid;
        const base = `${a.page}_${a.type}`;

        if (a.type === 'key_value') {
            const v = a.value?.bbox?.join('_') || 'no_val_bbox';
            return `${base}_${v}`;
        }

        if (a.type === 'entity' || a.type === 'summary_row') return `${base}_${a.bbox?.join('_')}`;
        if (a.type === 'table_row' && Array.isArray(a.columns)) {
            const coords = a.columns.map(col => col.bbox?.join('_')).join('|');
            return `${base}_${coords}`;
        }
        if (a.type === 'table' && Array.isArray(a.bbox)) return `${base}_${a.bbox.join('_')}`;
        return `${base}_fallback_${Math.random().toString(36).slice(2, 8)}`;
    };


    // --- NEW: compute per-page group outlines over ALL group members (always visible) ---
    const groupOutlines = useMemo(() => {
        // Build map of groupId -> members (on THIS page)
        const byGroup = new Map();
        for (const ph of pageHighlights) {
            if (!ph?.groupId) continue;
            if (!byGroup.has(ph.groupId)) byGroup.set(ph.groupId, []);
            byGroup.get(ph.groupId).push(ph);
        }
        const selectedKeySet = new Set((selectedAnnotations ?? []).map(getAnnotationKey));

        const outlines = [];
        for (const [groupId, members] of byGroup.entries()) {
            // Collect all bboxes from members for union
            const boxes = [];
            for (const m of members) {
                if (Array.isArray(m.bbox)) boxes.push(m.bbox);
                if (m.type === 'key_value') {
                    if (m.field?.bbox) boxes.push(m.field.bbox);
                    if (m.value?.bbox) boxes.push(m.value.bbox);
                }
            }
            if (!boxes.length) continue;
            const x0 = Math.min(...boxes.map(b => b[0]));
            const y0 = Math.min(...boxes.map(b => b[1]));
            const x1 = Math.max(...boxes.map(b => b[2]));
            const y1 = Math.max(...boxes.map(b => b[3]));

            // Selection state for styling & click behavior
            const selCount = members.reduce((n, m) => n + (selectedKeySet.has(getAnnotationKey(m)) ? 1 : 0), 0);
            const allSelected = selCount === members.length && members.length > 0;
            const anySelected = selCount > 0;

            outlines.push({groupId, bbox: [x0, y0, x1, y1], anySelected, allSelected, size: members.length});
        }
        return outlines;
    }, [pageHighlights, selectedAnnotations]);


    // Drag handlers
    const handleMouseDown = (e, h, box) => {
        const annotationKey = getAnnotationKey(h);
        const isSelected = isAnnotationSelected(h);

        // Only allow dragging if not in drawing or multi-select mode and annotation is selected
        // Also prevent dragging while annotation is being processed
        if (isDrawing || multiSelectMode || !isSelected || h.status === 'loading') {
            return;
        }

        e.stopPropagation();
        e.preventDefault(); // Prevent browser's default drag behavior

        const [x0, y0, x1, y1] = box.bbox;
        const left = x0 * scale - MARGIN;
        const top = y0 * scale - MARGIN;

        setDragging({annotation: h, box: box, key: annotationKey});
        dragStartPos.current = {x: e.clientX, y: e.clientY};
        setDragOffset({x: left, y: top});
    };

    const handleMouseMove = (e) => {
        if (!dragging) return;

        const dx = e.clientX - dragStartPos.current.x;
        const dy = e.clientY - dragStartPos.current.y;

        const [x0, y0] = dragging.box.bbox;
        setDragOffset({
            x: x0 * scale - MARGIN + dx,
            y: y0 * scale - MARGIN + dy
        });
    };

    const handleMouseUp = (e) => {
        if (!dragging) return;

        const dx = e.clientX - dragStartPos.current.x;
        const dy = e.clientY - dragStartPos.current.y;

        // Only reprocess if the annotation was actually moved (threshold: 5 pixels)
        const hasMoved = Math.abs(dx) > 5 || Math.abs(dy) > 5;

        if (hasMoved) {
            // Convert pixel delta back to PDF coordinates
            const dxPdf = dx / scale;
            const dyPdf = dy / scale;

            // Update the annotation's bbox
            const [x0, y0, x1, y1] = dragging.box.bbox;
            const newBbox = [x0 + dxPdf, y0 + dyPdf, x1 + dxPdf, y1 + dyPdf];

            // Call update handler if provided
            if (typeof onUpdateAnnotationPosition === 'function') {
                onUpdateAnnotationPosition(dragging.key, dragging.annotation, dragging.box.type, newBbox, true);
            }
        }

        setDragging(null);
        setDragOffset({x: 0, y: 0});
    };

    // Resize handlers
    const handleResizeStart = (e, h, box, handle) => {
        if (isDrawing || multiSelectMode || h.status === 'loading') return;

        e.stopPropagation();
        if (!isAnnotationSelected(h)) onBoxClick?.(h);

        const annotationKey = getAnnotationKey(h);
        setResizing({
            annotation: h,
            box: box,
            key: annotationKey,
            handle: handle,
            startBbox: [...box.bbox],
            startMouse: {x: e.clientX, y: e.clientY}
        });

        const [x0, y0, x1, y1] = box.bbox;
        setResizePreview({
            left: x0 * scale - MARGIN,
            top: y0 * scale - MARGIN,
            width: (x1 - x0) * scale + MARGIN * 2,
            height: (y1 - y0) * scale + MARGIN * 2
        });
    };

    const handleResizeMove = (e) => {
        if (!resizing) return;

        const dx = e.clientX - resizing.startMouse.x;
        const dy = e.clientY - resizing.startMouse.y;

        const [x0, y0, x1, y1] = resizing.startBbox;
        let newX0 = x0, newY0 = y0, newX1 = x1, newY1 = y1;

        // Update bbox based on which handle is being dragged
        const handle = resizing.handle;
        const dxPdf = dx / scale;
        const dyPdf = dy / scale;

        if (handle.includes('n')) newY0 = y0 + dyPdf;
        if (handle.includes('s')) newY1 = y1 + dyPdf;
        if (handle.includes('w')) newX0 = x0 + dxPdf;
        if (handle.includes('e')) newX1 = x1 + dxPdf;

        // Ensure minimum size (20 pixels in PDF coordinates)
        const minSize = 20 / scale;
        if (newX1 - newX0 < minSize) {
            if (handle.includes('w')) newX0 = newX1 - minSize;
            if (handle.includes('e')) newX1 = newX0 + minSize;
        }
        if (newY1 - newY0 < minSize) {
            if (handle.includes('n')) newY0 = newY1 - minSize;
            if (handle.includes('s')) newY1 = newY0 + minSize;
        }

        setResizePreview({
            left: newX0 * scale - MARGIN,
            top: newY0 * scale - MARGIN,
            width: (newX1 - newX0) * scale + MARGIN * 2,
            height: (newY1 - newY0) * scale + MARGIN * 2
        });
    };

    const handleResizeEnd = (e) => {
        if (!resizing) return;

        const dx = e.clientX - resizing.startMouse.x;
        const dy = e.clientY - resizing.startMouse.y;

        // Only reprocess if actually resized (threshold: 5 pixels)
        const hasResized = Math.abs(dx) > 5 || Math.abs(dy) > 5;

        if (hasResized && resizePreview) {
            const dxPdf = dx / scale;
            const dyPdf = dy / scale;

            const [x0, y0, x1, y1] = resizing.startBbox;
            let newX0 = x0, newY0 = y0, newX1 = x1, newY1 = y1;

            const handle = resizing.handle;
            if (handle.includes('n')) newY0 = y0 + dyPdf;
            if (handle.includes('s')) newY1 = y1 + dyPdf;
            if (handle.includes('w')) newX0 = x0 + dxPdf;
            if (handle.includes('e')) newX1 = x1 + dxPdf;

            // Ensure minimum size
            const minSize = 20 / scale;
            if (newX1 - newX0 < minSize) {
                if (handle.includes('w')) newX0 = newX1 - minSize;
                if (handle.includes('e')) newX1 = newX0 + minSize;
            }
            if (newY1 - newY0 < minSize) {
                if (handle.includes('n')) newY0 = newY1 - minSize;
                if (handle.includes('s')) newY1 = newY0 + minSize;
            }

            const newBbox = [newX0, newY0, newX1, newY1];

            if (typeof onUpdateAnnotationPosition === 'function') {
                onUpdateAnnotationPosition(resizing.key, resizing.annotation, resizing.box.type, newBbox, true);
            }
        }

        setResizing(null);
        setResizePreview(null);
    };

    // Add mouse event listeners for dragging and resizing
    React.useEffect(() => {
        if (!dragging && !resizing) return;

        const handleGlobalMouseMove = (e) => {
            if (dragging) handleMouseMove(e);
            if (resizing) handleResizeMove(e);
        };

        const handleGlobalMouseUp = (e) => {
            if (dragging) handleMouseUp(e);
            if (resizing) handleResizeEnd(e);
        };

        window.addEventListener('mousemove', handleGlobalMouseMove);
        window.addEventListener('mouseup', handleGlobalMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [dragging, resizing]);

    const renderBoxes = (h) => {
        const isTempGroup = groupingTempKeys?.includes(getAnnotationKey(h));
        const isSelected = isAnnotationSelected(h);
        const isHovered = hoveredKey === getAnnotationKey(h);

        const boxesToRender = [];

        if (Array.isArray(h.bbox)) {
            boxesToRender.push({bbox: h.bbox, text: h.text, type: h.type});
        }

        if (h.type === 'key_value') {
            if (h.field?.bbox) {
                boxesToRender.push({bbox: h.field.bbox, text: h.field.text, type: 'key_value_field'});
            }
            if (h.value?.bbox) {
                boxesToRender.push({bbox: h.value.bbox, text: h.value.text, type: 'key_value_value'});
            }
        }

        // NEW: render each group's items like individual value boxes
        if (h.type === 'key_value_group' && Array.isArray(h.items)) {
            for (const item of h.items) {
                const vb = item?.value?.bbox;
                if (Array.isArray(vb) && vb.length === 4) {
                    boxesToRender.push({
                        bbox: vb,
                        text: item?.value?.text ?? '',
                        type: 'key_value_value'
                    });
                }
            }
        }

        return boxesToRender.map((box, j) => {
            const annotationKey = getAnnotationKey(h);
            const resizeKey = `${annotationKey}-${j}`;
            const isDraggingThis = dragging && dragging.key === annotationKey && dragging.box === box;
            const isResizingThis = resizing && resizing.key === annotationKey && resizing.box === box;

            const [x0, y0, x1, y1] = box.bbox;
            const left = x0 * scale - MARGIN;
            const top = y0 * scale - MARGIN;
            const width = (x1 - x0) * scale + MARGIN * 2;
            const height = (y1 - y0) * scale + MARGIN * 2;

            const style = getStyleForType(box.type);
            const showResizeHandles = resizeHoverKey === resizeKey &&
                !isDrawing &&
                !multiSelectMode &&
                !isDraggingThis &&
                !isResizingThis &&
                h.status !== 'loading';

            const baseStyle = {
                backgroundColor: isTempGroup
                    ? 'rgba(242, 140, 140, 0.3)'
                    : isHovered && isSelected
                        ? 'rgba(244, 197, 66, 0.35)' // ⚠️ Warning yellow/gold for hovered AND selected
                        : isHovered
                            ? 'rgba(13, 148, 136, 0.25)' // Darker green for hovered but not selected
                            : isSelected
                                ? 'rgba(59, 188, 167, 0.1)' // ✅ Greenish for selected
                                : style.backgroundColor,
                border: isTempGroup
                    ? '1px solid #f28c8c'
                    : isHovered && isSelected
                        ? '2px solid #f4c542' // ⚠️ Warning color border for hovered AND selected
                        : isHovered
                            ? '2px solid #0d9488' // Darker green border for hovered but not selected
                            : isSelected
                                ? '1px solid #0d9488'
                                : style.border
            };

            const isPeek = peekSet.has(getAnnotationKey(h));
            return (
                <Box
                    key={`${getAnnotationKey(h)}-${j}`}
                    sx={{
                        position: 'absolute',
                        left,
                        top,
                        width,
                        height,
                        ...style,
                        ...baseStyle,
                        // brief wiggle when peekKey matches
                        animation: isPeek ? 'wiggle 380ms ease-in-out 1' : 'none',
                        willChange: isPeek ? 'transform' : 'auto',
                        opacity: isDraggingThis || isResizingThis ? 0.3 : 1,
                        cursor: h.status === 'loading' ? 'wait' : (isSelected && !isDrawing && !multiSelectMode ? 'move' : 'pointer'),
                    }}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({
                            mouseX: e.clientX + 2,
                            mouseY: e.clientY - 6,
                        });
                        setContextAnnotation(h);
                        setContextBoxText(box.text || '');
                    }}
                    onMouseLeave={() => {
                        onBoxHover?.(null);
                        hideResizeAffordanceSoon();
                    }}
                >
                    <Box
                        component="div"
                        draggable={false}
                        onClick={() => onBoxClick(h)}
                        onMouseDown={(e) => handleMouseDown(e, h, box)}
                        onMouseEnter={() => {
                            onBoxHover?.(annotationKey);
                            showResizeAffordance(resizeKey);
                        }}
                        onDragStart={(e) => e.preventDefault()}
                        title={box.text}
                        sx={{
                            width: '100%',
                            height: '100%',
                            cursor: h.status === 'loading' ? 'wait' : (isSelected && !isDrawing && !multiSelectMode ? 'move' : 'pointer'),
                            userSelect: 'none', // Prevent text selection during drag
                        }}
                    />

                    {/* Resize handles - only show when selected and not in drawing/grouping/dragging/resizing/loading mode */}
                    {showResizeHandles && (
                        <>
                            {/* Corner-only resize handles, close to the annotation corners */}
                            {['nw', 'ne', 'sw', 'se'].map(handle => {
                                const handleSize = 12;
                                const handleOffset = 5;
                                const handlePos = {
                                    nw: {top: -handleOffset, left: -handleOffset, cursor: 'nw-resize'},
                                    ne: {top: -handleOffset, right: -handleOffset, cursor: 'ne-resize'},
                                    sw: {bottom: -handleOffset, left: -handleOffset, cursor: 'sw-resize'},
                                    se: {bottom: -handleOffset, right: -handleOffset, cursor: 'se-resize'},
                                };
                                return (
                                    <Box
                                        key={`handle-${handle}`}
                                        onMouseEnter={() => showResizeAffordance(resizeKey)}
                                        onMouseLeave={hideResizeAffordanceSoon}
                                        onMouseDown={(e) => handleResizeStart(e, h, box, handle)}
                                        sx={{
                                            position: 'absolute',
                                            ...handlePos[handle],
                                            width: handleSize,
                                            height: handleSize,
                                            backgroundColor: '#0d9488',
                                            border: '2px solid white',
                                            borderRadius: '2px',
                                            cursor: handlePos[handle].cursor,
                                            zIndex: 1001,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                            '&:hover': {
                                                transform: 'scale(1.15)',
                                            },
                                            transition: 'all 0.1s ease',
                                        }}
                                    />
                                );
                            })}
                        </>
                    )}

                </Box>

            );
        });
    };

    return (
        <>
            {/* wiggle keyframes used when a peek is requested */}
            <style>
                {`
          @keyframes wiggle {
            0% { transform: translateX(0); }
            15% { transform: translateX(-2px); }
            30% { transform: translateX(2px); }
            45% { transform: translateX(-2px); }
            60% { transform: translateX(2px); }
            75% { transform: translateX(-1px); }
            100% { transform: translateX(0); }
          }
        `}
            </style>
            {/* --- Render group outlines first (under individual boxes), always visible --- */}
            {groupOutlines.map(({groupId, bbox, anySelected, allSelected, size}) => {
                const [x0, y0, x1, y1] = bbox;
                const left = x0 * scale - MARGIN * 2;
                const top = y0 * scale - MARGIN * 2;
                const width = (x1 - x0) * scale + MARGIN * 4;
                const height = (y1 - y0) * scale + MARGIN * 4;

                // Distinct style for group container:
                // - lighter when none selected
                // - stronger when any selected
                const outlineStyle = {
                    position: 'absolute',
                    left, top, width, height,
                    border: '2px dashed #7c3aed',
                    opacity: anySelected ? 0.9 : 0.35,
                    backgroundColor: 'transparent',
                    borderRadius: '6px',
                    pointerEvents: 'auto',
                    zIndex: 1,
                };

                // Clicking the group outline toggles all members on this page
                const handleOutlineClick = (e) => {
                    e.stopPropagation();
                    const groupMembers = pageHighlights.filter(ph => ph.groupId === groupId);
                    if (!groupMembers.length) return;
                    const selectedKeySet = new Set((selectedAnnotations ?? []).map(getAnnotationKey));
                    const areAllSelected = groupMembers.every(m => selectedKeySet.has(getAnnotationKey(m)));
                    if (areAllSelected) {
                        // Unselect all
                        groupMembers.forEach(m => {
                            if (selectedKeySet.has(getAnnotationKey(m))) onBoxClick(m);
                        });
                    } else {
                        // Select all
                        groupMembers.forEach(m => {
                            if (!selectedKeySet.has(getAnnotationKey(m))) onBoxClick(m);
                        });
                    }
                };

                return (
                    <Box key={`group-outline-${groupId}`}
                         sx={outlineStyle}
                         onContextMenu={(e) => handleGroupContextMenu(e, groupId)}
                         onClick={handleOutlineClick}
                         title={anySelected ? "Toggle entire group (selected)" : "Toggle entire group"}
                    />
                );
            })}
            {([...pageHighlights]
                .sort((a, b) => {
                    const order = {table: 0, key_value: 1, entity: 1, summary_row: 1};
                    return (order[a.type] ?? 2) - (order[b.type] ?? 2);
                }))
                .flatMap(renderBoxes)}

            {/* Drag preview box */}
            {dragging && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: dragOffset.x,
                        top: dragOffset.y,
                        width: (dragging.box.bbox[2] - dragging.box.bbox[0]) * scale + MARGIN * 2,
                        height: (dragging.box.bbox[3] - dragging.box.bbox[1]) * scale + MARGIN * 2,
                        border: '2px solid #0d9488',
                        backgroundColor: 'rgba(59, 188, 167, 0.3)',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                />
            )}

            {/* Resize preview box */}
            {resizing && resizePreview && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: resizePreview.left,
                        top: resizePreview.top,
                        width: resizePreview.width,
                        height: resizePreview.height,
                        border: '2px dashed #0d9488',
                        backgroundColor: 'rgba(59, 188, 167, 0.2)',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                />
            )}

            {currentRect && isCurrentPage && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: currentRect.x,
                        top: currentRect.y,
                        width: currentRect.width,
                        height: currentRect.height,
                        border: multiSelectMode ? '2px dashed #f28c8c' : '2px dashed #0d9488',
                        backgroundColor: multiSelectMode ? 'rgba(242, 140, 140, 0.2)' : 'rgba(59, 188, 167, 0.2)',
                        pointerEvents: 'none',
                        zIndex: 10,
                    }}
                />
            )}
            <Menu
                open={contextMenu !== null}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? {top: contextMenu.mouseY, left: contextMenu.mouseX}
                        : undefined
                }
            >
                <MenuItem
                    onClick={() => {
                        navigator.clipboard.writeText(contextBoxText || '');
                        setContextMenu(null);
                    }}
                    disabled={
                        contextAnnotation?.isGroupAction ||
                        contextAnnotation?.type === 'table' ||
                        contextAnnotation?.type === 'table_row'} sx={{
                    fontSize: '0.75rem',         // Smaller text
                    py: 0,                     // Less vertical padding
                    px: 1.2,                     // Less horizontal padding
                    minHeight: '32px',           // Shorter overall
                }}
                >
                    <ContentCopyIcon sx={{fontSize: 16, mr: 0.5}}/>
                    {t("copy_value")}
                </MenuItem>

                {contextAnnotation?.groupId && <Divider sx={{my: 0}}/>}
                {contextAnnotation?.groupId && (
                    <MenuItem
                        onClick={() => {
                            if (contextAnnotation?.isGroupAction) {
                                const members = pageHighlights.filter(ph => ph.groupId === contextAnnotation.groupId);
                                if (typeof onDissociateAnnotation === 'function') {
                                    members.forEach(member => onDissociateAnnotation(member));
                                }
                            } else if (typeof onDissociateAnnotation === 'function' && contextAnnotation) {
                                onDissociateAnnotation(contextAnnotation);
                            }
                            setContextMenu(null);
                        }}
                        sx={{
                            fontSize: '0.75rem',
                            py: 0,
                            px: 1.2,
                            minHeight: '32px',
                        }}
                    >
                        <LinkOffIcon sx={{fontSize: 16, mr: 0.5}}/>
                        {contextAnnotation?.isGroupAction ? t("ungroup_all") : t("dissociate_from_group")}
                    </MenuItem>
                )}

                <Divider sx={{my: 0}}/>
                <MenuItem
                    onClick={() => {
                        if (contextAnnotation?.isGroupAction) {
                            const members = pageHighlights.filter(ph => ph.groupId === contextAnnotation.groupId);
                            if (typeof onRemoveAnnotation === 'function') {
                                members.forEach(member => onRemoveAnnotation(member));
                            }
                        } else if (typeof onRemoveAnnotation === 'function' && contextAnnotation) {
                            onRemoveAnnotation(contextAnnotation);
                        }
                        setContextMenu(null);
                    }}
                    sx={{
                        fontSize: '0.75rem',
                        py: 0,
                        px: 1.2,
                        minHeight: '32px',
                        color: 'error.main', // Use theme color for delete
                    }}
                >
                    <DeleteIcon sx={{fontSize: 16, mr: 0.5}}/>
                    {contextAnnotation?.isGroupAction ? t("delete_group") : t("delete_annotation")}
                </MenuItem>
            </Menu>
        </>
    );
}

export default HighlightOverlay;
